import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, MessageCircle, Sparkles, Star, Heart, ChevronRight, LogIn, Home, Check, ArrowRight, X, Info } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/6bb31c52-7f71-42b6-a90b-b35b59eafbef/images/ebc96060859b79f8f1d6b9821b50f78cd5184dbe101cdbfb98b6a77a681e0f36.png";
const ABOUT_IMG = "https://images.pexels.com/photos/32381921/pexels-photo-32381921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const WHATSAPP_NUM = "966552221465";
const PHONE_DISPLAY = "+966 55 222 1465";
const MAP_URL = "https://maps.app.goo.gl/RhuFQJePZvUm5Bia6";
const MAP_EMBED = "https://www.google.com/maps?q=Java+Spa+Center+Prince+Ahmed+bin+Abdulaziz+King+Fahd+Riyadh&output=embed";
const ADDRESS_LINE_1 = "Prince Ahmed bin Abdulaziz Rd";
const ADDRESS_LINE_2 = "King Fahd, Riyadh 11421";

export default function LandingPage() {
  const { t, lang, rtl } = useLang();
  const [services, setServices] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/announcements", { params: { active_only: true } })
      .then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);

  const serviceName = (s) => lang === "ar" ? s.name_ar : lang === "en" && s.name_en ? s.name_en : s.name_id;
  const serviceDescription = (s) => {
    if (lang === "ar") return s.description_ar || s.description_id || s.description_en || "";
    if (lang === "en") return s.description_en || s.description_id || s.description_ar || "";
    return s.description_id || s.description_en || s.description_ar || "";
  };

  return (
    <div className="min-h-screen bg-charcoal-800 text-foreground">
      {/* Sticky Header */}
      <header className="fixed inset-x-0 top-0 z-50 backdrop-blur-xl bg-charcoal-900/70 border-b border-white/5">
        <div className="h-0.5 bg-gradient-to-r from-gold via-leaf to-gold" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <BrandLogo size="sm" />
          <div className="flex items-center gap-2 md:gap-3">
            <LanguageSwitcher />
            <Link
              to="/login"
              data-testid="header-login-link"
              title={t("cta.login")}
              aria-label={t("cta.login")}
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-gold/40 text-gold hover:text-charcoal-900 hover:bg-gold transition-all active:scale-95 shadow-[0_4px_14px_-4px_rgba(212,175,55,0.3)] hover:shadow-[0_6px_20px_-4px_rgba(212,175,55,0.6)]"
            >
              <LogIn className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center grain-overlay">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900 via-charcoal-900/70 to-charcoal-900/30" />
          <div className="absolute inset-0 bg-charcoal-900/30" />
        </div>
        <div className="relative max-w-7xl mx-auto w-full px-5 md:px-8 pt-32 pb-16 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8 animate-fade-in-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold border border-gold/30 rounded-full px-3 py-1.5">
              <Sparkles className="h-3.5 w-3.5" /> {t("app.tagline")}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-heading font-light leading-[1.05] tracking-tight">
              <span className="block text-foreground/95">{t("landing.hero.title").split(" ").slice(0, -1).join(" ")}</span>
              <span className="block gold-leaf-shimmer">{t("landing.hero.title").split(" ").slice(-1)}</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl max-w-2xl text-foreground/70 leading-relaxed">
              {t("landing.hero.sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={`https://wa.me/${WHATSAPP_NUM}`}
                target="_blank" rel="noreferrer"
                data-testid="hero-whatsapp-btn"
                className="btn-gold"
              >
                <MessageCircle className="h-5 w-5" /> {t("cta.book")}
              </a>
              <a href="#services" className="btn-outline-gold" data-testid="hero-services-link">
                {t("landing.services")} <ChevronRight className={`h-4 w-4 ${rtl ? "rotate-180" : ""}`} />
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-foreground/60">
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-leaf" /> {t("landing.contact.hours")}</span>
              <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-gold" /> {PHONE_DISPLAY}</span>
              <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" /> King Fahd, Riyadh</span>
            </div>
          </div>
          <div className="md:col-span-4 mt-4 md:mt-0">
            <div className="card-luxury accent-gold p-5 md:p-6 backdrop-blur-xl bg-charcoal-700/70">
              <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3 flex items-center gap-2">
                <Star className="h-3 w-3 fill-gold" /> Premium <span className="text-leaf">·</span> VIP
              </div>
              <div className="space-y-3">
                {["Pijat VIP", "Hammam VIP", "Hammam + Pijat + Pedikur"].map((n, i) => {
                  const s = services.find(x => x.name_id === n);
                  if (!s) return null;
                  return (
                    <div key={n} className="flex justify-between items-center pb-3 border-b border-gold/10 last:border-0 last:pb-0">
                      <div>
                        <div className="font-medium">{serviceName(s)}</div>
                        <div className="text-xs text-foreground/50">{s.duration_min} {t("common.minutes")}</div>
                      </div>
                      <div className="text-gold font-semibold">{formatSAR(s.price)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Announcements */}
      {announcements.length > 0 && (
        <section className="bg-gradient-to-r from-gold/10 via-leaf/5 to-gold/10 border-y border-gold/20 py-6">
          <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold whitespace-nowrap">
              <Star className="h-4 w-4 fill-gold" /> {t("landing.discounts")}
            </div>
            <div className="flex-1 flex flex-wrap gap-x-8 gap-y-2" data-testid="announcements-marquee">
              {announcements.map((a) => (
                <div key={a.id} className="text-sm">
                  <span className="text-gold font-semibold me-2">{a.title}:</span>
                  <span className="text-foreground/80">{a.message}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section id="services" className="relative py-24 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-gold">— {t("landing.services")}</span>
              <h2 className="mt-3 text-3xl md:text-5xl font-heading font-light">{t("landing.services")}</h2>
              <p className="mt-2 text-foreground/60 max-w-xl">{t("landing.services.sub")}</p>
            </div>
            <div className="text-foreground/40 text-sm">
              {services.length} {lang === "ar" ? "خدمة" : lang === "en" ? "services" : "layanan"}
            </div>
          </div>

          {/* ───────── HOME CENTER — Flagship featured service ───────── */}
          <div
            data-testid="home-center-card"
            className="relative overflow-hidden rounded-3xl mb-10 group"
          >
            {/* Gradient background with grain */}
            <div className="absolute inset-0 bg-gradient-to-br from-gold via-gold-dark to-leaf opacity-95" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(0,0,0,0.35),transparent_55%)]" />
            <div className="absolute inset-0 opacity-20"
              style={{ backgroundImage: "radial-gradient(rgba(0,0,0,0.4) 1px, transparent 1px)", backgroundSize: "4px 4px" }} />

            <div className="relative grid md:grid-cols-5 gap-6 p-6 md:p-10 lg:p-12">
              <div className="md:col-span-3 text-charcoal-900">
                <div className="inline-flex items-center gap-2 bg-charcoal-900/15 backdrop-blur rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-semibold">
                  <Sparkles className="h-3 w-3" /> {t("homecenter.badge")}
                </div>
                <h3 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-heading font-bold leading-[0.95] tracking-tight">
                  {t("homecenter.title")}
                </h3>
                <p className="mt-3 text-lg md:text-xl font-medium opacity-90">{t("homecenter.subtitle")}</p>
                <p className="mt-4 text-sm md:text-base opacity-80 max-w-lg leading-relaxed">{t("homecenter.desc")}</p>

                <div className="mt-6 space-y-2.5">
                  {[t("homecenter.feature1"), t("homecenter.feature2"), t("homecenter.feature3")].map((f) => (
                    <div key={f} className="flex items-center gap-2.5 text-sm md:text-base">
                      <div className="h-6 w-6 rounded-full bg-charcoal-900/15 grid place-items-center shrink-0">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </div>
                      <span className="font-medium">{f}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={`https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent("Halo Java SPA, saya ingin pesan layanan *Home Center* — terapis ke rumah. Mohon info layanan, durasi, dan harga.")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="home-center-cta"
                  className="mt-7 inline-flex items-center gap-2.5 bg-charcoal-900 text-gold hover:bg-charcoal-700 px-7 py-3.5 rounded-full font-semibold text-base shadow-[0_12px_30px_-8px_rgba(0,0,0,0.5)] hover:shadow-[0_16px_40px_-8px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 transition-all active:scale-95"
                >
                  <MessageCircle className="h-5 w-5" />
                  {t("homecenter.cta")}
                  <ArrowRight className={`h-4 w-4 ${rtl ? "rotate-180" : ""}`} />
                </a>
              </div>

              <div className="md:col-span-2 flex flex-col justify-between gap-5">
                {/* Big home icon visual */}
                <div className="relative flex items-center justify-center h-32 md:h-40">
                  <div className="absolute inset-0 bg-charcoal-900/10 rounded-2xl backdrop-blur-sm" />
                  <Home className="relative h-20 w-20 md:h-28 md:w-28 text-charcoal-900" strokeWidth={1.4} />
                </div>

                {/* Price card */}
                <div className="bg-charcoal-900/20 backdrop-blur-md border border-charcoal-900/15 rounded-2xl p-5 text-charcoal-900">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-70">{t("homecenter.priceLabel")}</div>
                  <div className="mt-1.5 text-2xl md:text-3xl font-heading font-bold">{t("homecenter.priceValue")}</div>
                  <div className="mt-3 pt-3 border-t border-charcoal-900/20 flex items-center gap-2 text-xs md:text-sm font-medium">
                    <Phone className="h-3.5 w-3.5" />
                    <span>{PHONE_DISPLAY}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ───────────────── End Home Center ─────────────────────── */}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s, i) => {
              const hasDesc = !!(s.description_id || s.description_ar || s.description_en);
              return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelectedService(s)}
                data-testid={`service-card-${s.id}`}
                className={`group card-luxury text-start ${s.is_vip ? "accent-gold" : i % 3 === 1 ? "accent-leaf" : ""} p-6 hover:-translate-y-1 transition-transform duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 cursor-pointer`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-foreground/40 mb-2">
                      {s.duration_min} {t("common.minutes")} {s.is_vip && <span className="ms-2 text-gold">★ VIP</span>}
                    </div>
                    <h3 className="text-xl font-heading">{serviceName(s)}</h3>
                    <div className="mt-1 text-sm text-foreground/50">{rtl ? s.name_id : s.name_ar}</div>
                  </div>
                  <Heart className={`h-5 w-5 ${i % 3 === 1 ? "text-leaf/40 group-hover:text-leaf" : "text-foreground/30 group-hover:text-gold"} transition-colors`} />
                </div>
                {hasDesc && (
                  <p className="mt-3 text-xs text-foreground/50 line-clamp-2">
                    {serviceDescription(s)}
                  </p>
                )}
                <div className="mt-6 pt-4 border-t border-gold/10 flex justify-between items-center">
                  <span className={`text-2xl font-heading ${i % 3 === 1 && !s.is_vip ? "text-leaf" : "text-gold"}`}>{formatSAR(s.price)}</span>
                  <span className="text-sm text-leaf group-hover:text-leaf-light inline-flex items-center gap-1">
                    <Info className="h-3.5 w-3.5" />
                    {lang === "ar" ? "التفاصيل" : lang === "en" ? "Details" : "Detail"}
                    <ChevronRight className={`h-3 w-3 ${rtl ? "rotate-180" : ""}`} />
                  </span>
                </div>
              </button>
            );})}
          </div>
        </div>
      </section>

      {/* CONTACT + MAP */}
      <section className="relative py-24 px-5 md:px-8 border-t border-gold/10 bg-charcoal-900">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-gold">— {t("landing.contact")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-heading font-light">{t("cta.contact")}</h2>

            <div className="mt-10 space-y-6">
              <div className="flex gap-4 items-start">
                <div className="h-11 w-11 rounded-full bg-gold/15 grid place-items-center"><Phone className="h-5 w-5 text-gold" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("landing.contact.phone")}</div>
                  <a href={`tel:+${WHATSAPP_NUM}`} className="text-xl font-heading text-foreground">{PHONE_DISPLAY}</a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="h-11 w-11 rounded-full bg-leaf/15 grid place-items-center"><Clock className="h-5 w-5 text-leaf" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("landing.contact.hours")}</div>
                  <div className="text-xl font-heading">24 / 7</div>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="h-11 w-11 rounded-full bg-gold/15 grid place-items-center"><MapPin className="h-5 w-5 text-gold" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("landing.location")}</div>
                  <a
                    href={MAP_URL} target="_blank" rel="noreferrer"
                    className="text-xl font-heading hover:text-gold transition block"
                    data-testid="map-link"
                  >{ADDRESS_LINE_1}</a>
                  <div className="text-sm text-foreground/60 mt-0.5">{ADDRESS_LINE_2}</div>
                </div>
              </div>
            </div>

            <a
              href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
              className="btn-gold mt-10"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="h-5 w-5" /> {t("cta.whatsapp")}
            </a>
          </div>

          <div className="rounded-2xl overflow-hidden border border-gold/20 h-[450px]">
            <iframe
              title="Java SPA location"
              src={MAP_EMBED}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gold/10 py-10 px-5 md:px-8 pb-24 md:pb-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <BrandLogo size="sm" />
          <div className="text-sm text-foreground/40">
            © {new Date().getFullYear()} Java SPA Riyadh · {t("landing.contact.hours")}
          </div>
          <Link to="/login" className="text-sm text-foreground/60 hover:text-gold" data-testid="footer-staff-login">
            {t("cta.login")} →
          </Link>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
        data-testid="floating-whatsapp"
        className="hidden md:grid fixed bottom-6 end-6 z-50 h-14 w-14 rounded-full bg-leaf hover:bg-leaf-light shadow-[0_8px_30px_-4px_rgba(109,190,69,0.5)] place-items-center transition-transform hover:-translate-y-1"
      >
        <MessageCircle className="h-7 w-7 text-white" />
      </a>

      {/* MOBILE sticky bottom CTA bar */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-50 bg-charcoal-900/95 backdrop-blur-xl border-t border-gold/30 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex gap-2">
          <a
            href={`tel:+${WHATSAPP_NUM}`}
            data-testid="mobile-cta-call"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full border border-gold/40 text-gold font-medium text-sm active:scale-95 transition"
          >
            <Phone className="h-4 w-4" /> Call
          </a>
          <a
            href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
            data-testid="mobile-cta-wa"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full bg-leaf text-white font-semibold text-sm active:scale-95 transition shadow-[0_8px_24px_-6px_rgba(109,190,69,0.5)]"
          >
            <MessageCircle className="h-4 w-4" /> {t("cta.book")}
          </a>
        </div>
      </div>

      {/* ─── Service Detail Modal ────────────────────────────────────── */}
      {selectedService && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md grid place-items-center p-4 animate-in fade-in"
          onClick={() => setSelectedService(null)}
          data-testid="service-detail-modal"
        >
          <div
            className={`relative w-full max-w-lg card-luxury ${selectedService.is_vip ? "accent-gold" : "accent-leaf"} p-6 md:p-8 max-h-[90vh] overflow-y-auto scrollbar-thin`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedService(null)}
              data-testid="service-detail-close"
              aria-label="Close"
              className="absolute top-3 end-3 p-2 rounded-full hover:bg-foreground/10 text-foreground/60 hover:text-foreground transition"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              {selectedService.duration_min} {t("common.minutes")}
              {selectedService.is_vip && <span className="ms-1 text-gold">★ VIP</span>}
            </div>

            <h3 className="text-2xl md:text-3xl font-heading text-gold">{serviceName(selectedService)}</h3>
            <div className="mt-1 text-sm text-foreground/50">
              {lang === "ar" ? selectedService.name_id : selectedService.name_ar}
              {lang !== "en" && selectedService.name_en && <span className="ms-2 text-foreground/30">· {selectedService.name_en}</span>}
            </div>

            {serviceDescription(selectedService) ? (
              <p className="mt-5 text-sm md:text-base text-foreground/80 leading-relaxed whitespace-pre-line" dir={lang === "ar" ? "rtl" : "ltr"}>
                {serviceDescription(selectedService)}
              </p>
            ) : (
              <p className="mt-5 text-sm text-foreground/40 italic">
                {lang === "ar" ? "لا يوجد وصف متاح." : lang === "en" ? "No description available." : "Belum ada keterangan."}
              </p>
            )}

            <div className="mt-8 pt-5 border-t border-gold/15 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/40">{t("services.price")}</div>
                <div className="text-3xl font-heading text-gold">{formatSAR(selectedService.price)}</div>
              </div>
              <a
                href={`https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(`Halo Java SPA, saya tertarik dengan ${serviceName(selectedService)} (${formatSAR(selectedService.price)}).`)}`}
                target="_blank" rel="noreferrer"
                data-testid="service-detail-book"
                className="btn-leaf px-6 py-3 w-full sm:w-auto justify-center"
              >
                <MessageCircle className="h-4 w-4" /> {t("cta.book")}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
